/**
 *  Copyright (C) 2022 - Jeci SARL - https://jeci.fr
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU Affero General Public License as
 *  published by the Free Software Foundation, either version 3 of the
 *  License, or (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU Affero General Public License for more details.
 *
 *  You should have received a copy of the GNU Affero General Public License
 *  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import { resolveAlias } from "@/services/aliasMap.js";

/**
 * Extracts a node ID from a nodeRef string.
 * Handles formats like "workspace://SpacesStore/<id>" → "<id>"
 * or plain IDs.
 */
function extractNodeId(nodeRef) {
  if (!nodeRef) {
    return nodeRef;
  }
  const parts = nodeRef.split("/");
  return parts[parts.length - 1];
}

/**
 * Derives the Alfresco node type from the ghost's known properties.
 * At upload time the full nodeType isn't available, but isFile/isFolder
 * tells us whether it's a cm:content or cm:folder (or subtype).
 */
function deriveGhostType(ghost) {
  if (ghost.nodeType) {
    return ghost.nodeType;
  }
  if (ghost.isFile) {
    return "cm:content";
  }
  if (ghost.isFolder) {
    return "cm:folder";
  }
  return null;
}

/**
 * Builds a lookup of effective property values for a ghost node by
 * combining explicit properties with values derived from upload metadata.
 */
function getEffectiveValue(ghost, resolvedProp) {
  // Check explicit properties first
  const explicit = ghost.properties?.[resolvedProp];
  if (explicit !== undefined && explicit !== null) {
    return explicit;
  }

  // Derive from known ghost fields
  switch (resolvedProp) {
    case "TYPE":
      return deriveGhostType(ghost);
    case "cm:name":
      return ghost.name || null;
    case "cm:content.mimetype":
      return ghost.content?.mimeType || null;
    case "cm:content.size":
      return ghost.content?.sizeInBytes ?? null;
    default:
      return null;
  }
}

/**
 * Attempts to match a ghost against a legacy raw AFTS query string.
 * Only evaluates TYPE clauses; returns true (optimistic) for queries
 * that don't contain TYPE.
 */
function matchesLegacyQuery(ghost, queryStr) {
  if (!queryStr) {
    return true;
  }

  // Extract all TYPE values from the query
  const typeRe = /TYPE\s*:\s*["']([^"']+)["']/gi;
  const allTypes = [];
  let match;
  while ((match = typeRe.exec(queryStr)) !== null) {
    allTypes.push(match[1]);
  }

  // No TYPE clause in query → can't evaluate, optimistic match
  if (allTypes.length === 0) {
    return true;
  }

  const ghostType = deriveGhostType(ghost);
  // Ghost type unknown → pessimistic, exclude
  if (!ghostType) {
    return false;
  }

  // Separate negated and positive TYPE values
  const negRe = /NOT\s+TYPE\s*:\s*["']([^"']+)["']/gi;
  const negatedTypes = new Set();
  let negMatch;
  while ((negMatch = negRe.exec(queryStr)) !== null) {
    negatedTypes.add(negMatch[1]);
  }

  const positiveTypes = allTypes.filter((t) => !negatedTypes.has(t));

  // Check negated types: ghost must NOT match any of them
  for (const t of negatedTypes) {
    if (isTypeMatch(ghostType, t)) {
      return false;
    }
  }

  // Check positive types: ghost must match at least one (if any exist)
  if (positiveTypes.length > 0) {
    return positiveTypes.some((t) => isTypeMatch(ghostType, t));
  }

  // Only negated types in query and ghost passed them all
  return true;
}

/**
 * Checks whether a ghost's nodeType matches a criterion type value.
 * A file (cm:content) can never match cm:folder and vice versa.
 * For other types (subtypes like mm:documentMarche), we can't determine
 * the type hierarchy client-side, so we match optimistically.
 */
function isTypeMatch(ghostType, criterionType) {
  if (ghostType === criterionType) {
    return true;
  }
  // A generic type (cm:content / cm:folder) cannot claim to be a specific
  // subtype (e.g. mm:documentMarche). Only an exact match or a known
  // parent→child relationship is valid. Since we don't have the type
  // hierarchy client-side, we reject when the ghost is generic but the
  // criterion asks for something more specific.
  if (
    (ghostType === "cm:content" || ghostType === "cm:folder") &&
    criterionType !== "cm:content" &&
    criterionType !== "cm:folder"
  ) {
    return false;
  }
  // If the ghost has a specific subtype (e.g. nodeType was set), accept
  // it only against a generic parent type of the same family.
  if (criterionType === "cm:content" && ghostType !== "cm:folder") {
    return true;
  }
  if (criterionType === "cm:folder" && ghostType !== "cm:content") {
    return true;
  }
  // Two different specific subtypes — no way to know if compatible
  return false;
}

/**
 * Stateless service that evaluates whether a ghost node matches
 * a view configuration (locations + criteriaGroups). Mirror of
 * AftsQueryBuilder but for client-side matching.
 */
class GhostMatcher {
  /**
   * Checks if a ghost node matches a full view config.
   * @param {Object} ghostNode - The ghost node with _uploadContext and properties.
   * @param {Object} viewConfig - View config with locations and criteriaGroups.
   * @returns {boolean}
   */
  matchesViewConfig(ghostNode, viewConfig) {
    if (!viewConfig) {
      return true;
    }
    if (!this._matchesLocations(ghostNode, viewConfig.locations)) {
      return false;
    }

    // Support both grouped and legacy flat criteria format
    if (viewConfig.criteriaGroups) {
      return this._matchesCriteriaGroups(ghostNode, viewConfig.criteriaGroups);
    }
    if (viewConfig.criteria) {
      return this._matchesFlatCriteria(ghostNode, viewConfig.criteria);
    }
    return true;
  }

  /**
   * Checks if a ghost node belongs to a specific folder.
   * @param {Object} ghostNode
   * @param {string} currentNodeId
   * @returns {boolean}
   */
  matchesFolder(ghostNode, currentNodeId) {
    return ghostNode._uploadContext?.parentNodeId === currentNodeId;
  }

  /**
   * Checks if a ghost node belongs to a specific site.
   * @param {Object} ghostNode
   * @param {string} siteId
   * @returns {boolean}
   */
  matchesSite(ghostNode, siteId) {
    return ghostNode._uploadContext?.siteId === siteId;
  }

  // --- Location matching ---

  _matchesLocations(ghost, locations) {
    if (!locations || locations.length === 0) {
      return true;
    }

    const inclusions = locations.filter((loc) => !loc.negation);
    const exclusions = locations.filter((loc) => loc.negation);

    // Inclusions: at least one must match (OR)
    if (inclusions.length > 0) {
      const anyInclusionMatches = inclusions.some((loc) =>
        this._matchesLocation(ghost, loc),
      );
      if (!anyInclusionMatches) {
        return false;
      }
    }

    // Exclusions: none must match (AND NOT)
    if (exclusions.length > 0) {
      const anyExclusionMatches = exclusions.some((loc) =>
        this._matchesLocation(ghost, loc),
      );
      if (anyExclusionMatches) {
        return false;
      }
    }

    return true;
  }

  _matchesLocation(ghost, loc) {
    const ctx = ghost._uploadContext;
    if (!ctx) {
      return false;
    }

    if (loc.type === "Site" && loc.value) {
      return ctx.siteId === loc.value;
    }

    if (loc.type === "Folder" && loc.value) {
      const folderId = extractNodeId(loc.value);
      if (ctx.parentNodeId === folderId) {
        return true;
      }
      if (
        Array.isArray(ctx.ancestorIds) &&
        ctx.ancestorIds.includes(folderId)
      ) {
        return true;
      }
      return false;
    }

    return false;
  }

  // --- Criteria matching ---

  _matchesFlatCriteria(ghost, criteria) {
    if (!criteria || criteria.length === 0) {
      return true;
    }
    // Legacy flat format: all criteria AND'd together
    return criteria.every((c) => this._matchesCriterion(ghost, c));
  }

  _matchesCriteriaGroups(ghost, criteriaGroups) {
    if (!criteriaGroups) {
      return true;
    }

    const groups = criteriaGroups.groups;
    if (!groups || groups.length === 0) {
      return true;
    }

    const rootOperator = (criteriaGroups.rootOperator || "OR").toUpperCase();

    if (rootOperator === "AND") {
      return groups.every((group) => this._matchesGroup(ghost, group));
    }
    // OR (default)
    return groups.some((group) => this._matchesGroup(ghost, group));
  }

  _matchesGroup(ghost, group) {
    if (!group.criteria || group.criteria.length === 0) {
      return true;
    }

    const operator = (group.operator || "AND").toUpperCase();

    if (operator === "OR") {
      return group.criteria.some((c) => this._matchesCriterion(ghost, c));
    }
    // AND (default)
    return group.criteria.every((c) => this._matchesCriterion(ghost, c));
  }

  _matchesCriterion(ghost, criterion) {
    // Legacy raw query — try to evaluate TYPE clauses, optimistic for the rest
    if (criterion.query) {
      return matchesLegacyQuery(ghost, criterion.query);
    }

    if (criterion.value === null || criterion.value === "") {
      return true;
    }

    const prop = resolveAlias(criterion.property?.id);
    const inputType = criterion.property?.input;
    const ghostValue = getEffectiveValue(ghost, prop);

    // If property is absent from ghost → pessimistic: ghost cannot prove it
    // belongs in this view, so exclude it. Ghosts uploaded in the current
    // view are already shown via the activeViewId shortcut in useGhostNodes.
    if (ghostValue === undefined || ghostValue === null) {
      return false;
    }

    // TYPE property uses special matching (parent-type awareness)
    if (prop === "TYPE") {
      const matches = isTypeMatch(String(ghostValue), String(criterion.value));
      return criterion.negation ? !matches : matches;
    }

    const matches = this._evaluateMatch(ghostValue, criterion, inputType);
    return criterion.negation ? !matches : matches;
  }

  _evaluateMatch(ghostValue, criterion, inputType) {
    switch (inputType) {
      case "boolean":
        return String(ghostValue) === String(criterion.value);

      case "date":
      case "datetime":
        return this._matchesDate(ghostValue, criterion);

      default:
        return this._matchesText(ghostValue, criterion);
    }
  }

  _matchesDate(ghostValue, criterion) {
    const ghostTime = new Date(ghostValue).getTime();
    if (isNaN(ghostTime)) {
      return true;
    }

    if (criterion.operator === "range") {
      let startTime = -Infinity;
      if (criterion.value) {
        const startOfDay = new Date(criterion.value);
        startOfDay.setUTCHours(0, 0, 0, 0);
        startTime = startOfDay.getTime();
      }

      let endTime = Infinity;
      if (criterion.value_end) {
        const endOfDay = new Date(criterion.value_end);
        endOfDay.setUTCHours(23, 59, 59, 999);
        endTime = endOfDay.getTime();
      }

      return ghostTime >= startTime && ghostTime <= endTime;
    }

    // Exact day match
    const dayStart = new Date(criterion.value);
    dayStart.setUTCHours(0, 0, 0, 0);
    const dayEnd = new Date(criterion.value);
    dayEnd.setUTCHours(23, 59, 59, 999);
    return ghostTime >= dayStart.getTime() && ghostTime <= dayEnd.getTime();
  }

  _matchesText(ghostValue, criterion) {
    const strValue = String(ghostValue);
    const criterionValue = String(criterion.value);

    switch (criterion.operator) {
      case "exact":
        return strValue === criterionValue;

      case "contains":
        return strValue.toLowerCase().includes(criterionValue.toLowerCase());

      case "range": {
        const start =
          criterion.value !== null && criterion.value !== ""
            ? criterion.value
            : null;
        const end =
          criterion.value_end !== null && criterion.value_end !== ""
            ? criterion.value_end
            : null;
        if (start !== null && strValue < String(start)) {
          return false;
        }
        if (end !== null && strValue > String(end)) {
          return false;
        }
        return true;
      }

      default:
        // No operator specified — optimistic match
        return true;
    }
  }
}

export default new GhostMatcher();
